// src/hooks/useFormElement.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useRef } from 'react';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import { clone } from 'utils/helpers';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

// *******************************************************************************
// FUNCTIONS
// *******************************************************************************
export const flattenName = name => {
  if (!name) return null;
  return Array.isArray(name)
    ? name.map(v => (typeof v === 'string' ? v : String(v))).join('.')
    : String(name).replace(/,/g, '.');
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
/**
 * Hook to manage form element properties with support for virtual fields
 * @param {string|array} name - Field name (e.g., 'zip', 'address1')
 * @param {string|array} subdocumentField - Parent subdocument field (optional)
 * @returns {object} Element properties including refs and field names
 *
 * Examples:
 * - useFormElement('zip') → name: 'zip'
 * - useFormElement('zip', 'address') → name: ['address', 'zip']
 * - useFormElement('zip', null, 'addresses_.main') → name: ['addresses_', 'main', 'zip']
 * - useFormElement('value', null, 'contacts_.mobile') → name: ['contacts_', 'mobile', 'value']
 */
const useFormElement = (name = `field-no-name`, subdocumentField) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // PERFORMANCE FIX: Only extract stable references (elRefs is a stable useState object)
  // goFocus is a useCallback with stable dependencies
  const { elRefs, goFocus } = useSimpleFormContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const elementRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  // Extract single field name - always the last part for proper label generation
  // Examples: 'zip' → 'zip', ['addresses_', 'main', 'zip'] → 'zip'
  const singleFieldName = Array.isArray(name) ? name[name.length - 1] : name;

  const fullFieldName = buildFullFieldName(name, subdocumentField);

  const flattenFieldName = flattenName(fullFieldName);

  const flattenParentFieldName = getParentFullField(flattenFieldName);

  const fullParentFieldName = getParentFullField(fullFieldName);

  const focus = delay => goFocus(flattenFieldName, delay);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    // register a concrete focusable node; tolerate wrappers (antd, masks, custom)
    let tries = 0;

    function resolveNode(refObj) {
      if (!refObj) return null;
      // antd Input
      if (refObj.input) return refObj.input;
      // antd TextArea (v5 legacy)
      if (refObj.resizableTextArea?.textArea) return refObj.resizableTextArea.textArea;
      // antd Picker / Select / TextArea (v6) — nativeElement wraps the actual input/textarea
      if (refObj.nativeElement) {
        return refObj.nativeElement.querySelector?.('input')
          || refObj.nativeElement.querySelector?.('textarea')
          || null;
      }
      // raw DOM element (input or textarea)
      if (refObj.tagName) return refObj;
      return null;
    }

    function domFallback() {
      if (typeof document === 'undefined') return null;
      const id = document.getElementById(flattenFieldName);
      const idTag = id?.tagName?.toLowerCase();
      if (idTag === 'input' || idTag === 'textarea') return id;
      if (id) {
        const inner = id.querySelector?.('input') || id.querySelector?.('textarea');
        if (inner) return inner;
      }
      const byName = document.querySelector?.(`input[name="${flattenFieldName}"]`)
        || document.querySelector?.(`textarea[name="${flattenFieldName}"]`);
      if (byName) return byName;
      const byProfile = document.querySelector?.(`input[profile="${flattenFieldName}"]`);
      if (byProfile) return byProfile;
      // AntD form-item container id ends with _<field>
      const container = document.querySelector?.(`[id$="_${flattenFieldName}"]`);
      const nested = container?.querySelector?.('input') || container?.querySelector?.('textarea');
      return nested || null;
    }

    function register() {
      if (!elRefs || !flattenFieldName) return;
      const node = resolveNode(elementRef.current) || domFallback();
      if (node) {
        elRefs[flattenFieldName] = node;
        return;
      }
      if (tries++ < 20) setTimeout(register, 50);
    }

    register();
  }, [flattenFieldName, elRefs]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return {
    elementRef,
    elRefs,
    singleFieldName,
    fullFieldName,
    flattenFieldName,
    flattenParentFieldName,
    fullParentFieldName,
    fieldId: flattenFieldName,
    focus,
  };
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default useFormElement;

// *******************************************************************************
// HELPERS
// *******************************************************************************
/**
 * Builds full field name considering subdocument, virtual field, and field name
 * @param {string|array} name - Field name
 * @param {string|array} subdocumentField - Parent subdocument field (optional)
 * @returns {string|array} Full field name path
 *
 * Logic:
 * 1. If subdocumentField exists, it's the root
 * 3. Add name at the end
 *
 * Examples:
 * - buildFullFieldName('zip', null, null) → 'zip'
 * - buildFullFieldName('zip', 'address', null) → ['address', 'zip']
 * - buildFullFieldName('zip', null, 'addresses_.main') → ['addresses_', 'main', 'zip']
 * - buildFullFieldName('value', null, 'contacts_.mobile') → ['contacts_', 'mobile', 'value']
 * - buildFullFieldName('zip', 'entity', 'addresses_.main') → ['entity', 'addresses_', 'main', 'zip']
 */
function buildFullFieldName(name, subdocumentField) {
  const parts = [];

  // 1. Add subdocument if exists
  if (subdocumentField) {
    if (Array.isArray(subdocumentField)) {
      parts.push(...subdocumentField);
    } else {
      parts.push(subdocumentField);
    }
  }

  // 3. Add field name
  const nameParts = Array.isArray(name) ? name : name?.split('.');
  parts.push(...nameParts);

  // If only one part, return as string (backward compatibility)
  return parts.length === 1 ? parts[0] : parts;
}

function getParentFullField(field) {
  let isArray = Array.isArray(field);
  let parts = isArray ? clone(field) : field.split('.');

  if (parts.length <= 1) {
    return null;
  } else {
    parts.pop();
    return isArray ? parts : parts.join('.');
  }
}
